"use client";

import type { AppRouter } from "@api/trpc/routers/_app";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  createTRPCClient,
  httpBatchStreamLink,
  httpLink,
  isNonJsonSerializable,
  loggerLink,
  splitLink,
} from "@trpc/client";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import posthog from "posthog-js";
import { useState } from "react";
import superjson from "superjson";
import { getQueryClient } from "@/lib/get-query-client";
import { createClient } from "@/utils/supabase/client";

const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>();

export { TRPCProvider, useTRPC };

const url = process.env.NEXT_PUBLIC_API_URL
  ? `https://${process.env.NEXT_PUBLIC_API_URL}`
  : `http://localhost:${3000}`;

export function TRPCReactProvider(
  props: Readonly<{
    children: React.ReactNode;
  }>,
) {
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        splitLink({
          condition: (op) => isNonJsonSerializable(op.input),
          true: httpLink({
            url: `${url}/trpc`,
            transformer: superjson,
            async headers() {
              const supabase = createClient();

              const {
                data: { session },
              } = await supabase.auth.getSession();

              const posthogSessionId = posthog.get_session_id?.();

              return {
                Authorization: `Bearer ${session?.access_token}`,
                "x-request-id": crypto.randomUUID(),
                ...(posthogSessionId && {
                  "x-posthog-session-id": posthogSessionId,
                }),
              };
            },
          }),
          false: httpBatchStreamLink({
            url: `${url}/trpc`,
            transformer: superjson,
            async headers() {
              const supabase = createClient();

              const {
                data: { session },
              } = await supabase.auth.getSession();

              const posthogSessionId = posthog.get_session_id?.();

              return {
                Authorization: `Bearer ${session?.access_token}`,
                "x-request-id": crypto.randomUUID(),
                ...(posthogSessionId && {
                  "x-posthog-session-id": posthogSessionId,
                }),
              };
            },
          }),
        }),
        loggerLink({
          enabled: (opts) =>
            process.env.NODE_ENV === "development" ||
            (opts.direction === "down" && opts.result instanceof Error),
        }),
      ],
    }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {props.children}
      </TRPCProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
